// The fake api's Reports tabs (F-ANL-03): `GET …/reports/range | revenue | sessions | products | staff`, answered with
// deterministic figures for any range — a busy gaming café whose weekends are fuller than its weekdays and whose
// evenings are its peak — so the console's tests, the axe scan and the screenshots read a realistic screen.
//
// The money follows the server's one revenue definition exactly: revenue = money in − top-ups + top-ups handed back +
// wallet spend, wallet spends counted as revenue, bonus spent apart. Every figure is derived from the business day's
// date, so the same range always answers the same report.

import { previousRangeOf, reportRangeOf, isReportPreset } from '@clavian/domain/reports';

/** The fake venue's business day today. */
export const FAKE_REPORTS_TODAY = '2026-10-01';

const INR = (amount: number) => ({ amount: Math.round(amount), currency: 'INR' });

/** A stable 0 … 1 for a key. */
function noise(key: string): number {
  let hash = 2166136261;
  for (const char of key) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return ((hash >>> 0) % 10_000) / 10_000;
}

/** Monday = 0 … Sunday = 6, of a `YYYY-MM-DD`. */
function weekdayOf(date: string): number {
  return (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
}

function daysOf(from: string, to: string): string[] {
  const days: string[] = [];
  for (let at = Date.parse(`${from}T00:00:00Z`); at <= Date.parse(`${to}T00:00:00Z`) && days.length < 400; at += 86_400_000) days.push(new Date(at).toISOString().slice(0, 10));
  return days;
}

/** How much busier a weekday is: Friday and Saturday nights are the café's best. */
const WEEKDAY_FACTOR = [0.82, 0.78, 0.86, 0.95, 1.28, 1.52, 1.18];

interface DayMoney {
  readonly date: string;
  readonly revenue: number;
  readonly walletSpent: number;
  readonly topUps: number;
  readonly handedBack: number;
  readonly bonusSpent: number;
  readonly bonusGranted: number;
  readonly refunds: number;
  readonly moneyIn: number;
}

function dayMoney(date: string, scale: number): DayMoney {
  if (date > FAKE_REPORTS_TODAY) return { date, revenue: 0, walletSpent: 0, topUps: 0, handedBack: 0, bonusSpent: 0, bonusGranted: 0, refunds: 0, moneyIn: 0 };
  const revenue = 2_100_000 * (WEEKDAY_FACTOR[weekdayOf(date)] ?? 1) * (0.86 + 0.28 * noise(`rev${date}`)) * scale;
  const walletSpent = revenue * (0.2 + 0.06 * noise(`wal${date}`));
  const topUps = revenue * (0.24 + 0.08 * noise(`top${date}`));
  const handedBack = noise(`hb${date}`) > 0.8 ? 50_000 * scale : 0;
  const bonusSpent = walletSpent * 0.07;
  const refunds = revenue * 0.012;
  return { date, revenue, walletSpent, topUps, handedBack, bonusSpent, bonusGranted: topUps * 0.08, refunds, moneyIn: revenue - walletSpent + topUps - handedBack };
}

function breakdown(days: readonly DayMoney[]) {
  const sum = (pick: (day: DayMoney) => number) => days.reduce((total, day) => total + pick(day), 0);
  const revenue = Math.round(sum((day) => day.revenue));
  const walletSpent = Math.round(sum((day) => day.walletSpent));
  const direct = revenue - walletSpent;
  const cash = Math.round(direct * 0.34);
  const card = Math.round(direct * 0.09);
  const online = Math.round(direct * 0.03);
  const upi = direct - cash - card - online;
  const topUps = Math.round(sum((day) => day.topUps));
  const handedBack = Math.round(sum((day) => day.handedBack));
  const moneyIn = revenue - walletSpent + topUps - handedBack;
  const refunds = Math.round(sum((day) => day.refunds));
  // Money in by tender: the direct payments and the top-ups (taken mostly by UPI and cash).
  const topUpCash = Math.round((topUps - handedBack) * 0.4);
  return {
    revenue: INR(revenue),
    payments: INR(revenue + refunds),
    refunds: INR(refunds),
    bySource: [
      { tender: 'cash', amount: INR(cash) },
      { tender: 'counter_card', amount: INR(card) },
      { tender: 'counter_upi', amount: INR(upi) },
      { tender: 'online', amount: INR(online) },
      { tender: 'wallet', amount: INR(walletSpent) },
    ],
    walletSpent: INR(walletSpent),
    moneyIn: INR(moneyIn),
    moneyInBySource: [
      { tender: 'cash', amount: INR(cash + topUpCash) },
      { tender: 'counter_card', amount: INR(card) },
      { tender: 'counter_upi', amount: INR(upi + (topUps - handedBack - topUpCash)) },
      { tender: 'online', amount: INR(online) },
    ],
    walletTopUps: INR(topUps),
    walletHandedBack: INR(handedBack),
    bonusSpent: INR(sum((day) => day.bonusSpent)),
    bonusGranted: INR(sum((day) => day.bonusGranted)),
  };
}

const VENUES = [
  { venueId: 'ven_01k5znw4hmfay8vvr14d2pf2dc', name: 'Playground Central', scale: 1 },
  { venueId: 'ven_01k5znw4hmfay8vvr14d2pf2dd', name: 'Playground North', scale: 0.62 },
];

export interface FakeReportsRequest {
  readonly pathname: string;
  readonly method: string;
  readonly searchParams: URLSearchParams;
  readonly permissions: readonly string[];
  readonly organizationPath: string;
  readonly venueId: string;
}

/** The answer to a Reports-tab request, or undefined for a path this does not serve. */
export function fakeReportsResponse(request: FakeReportsRequest): { readonly status: number; readonly body: unknown } | undefined {
  const { pathname, method, searchParams, permissions, organizationPath } = request;
  const match = new RegExp(`^${organizationPath}/reports/(range|revenue|sessions|products|staff)$`).exec(pathname);
  if (match === null || method !== 'GET') return undefined;
  const which = match[1] as 'range' | 'revenue' | 'sessions' | 'products' | 'staff';
  const needs = which === 'range' || which === 'sessions' ? 'reports:read' : 'reports:financial';
  if (!permissions.includes(needs)) return { status: 403, body: { type: 'https://clavian.io/problems/forbidden', title: 'Not allowed', status: 403, code: 'forbidden' } };

  if (which === 'range') {
    const preset = searchParams.get('preset') ?? 'last7';
    if (!isReportPreset(preset)) return { status: 400, body: { title: 'Invalid preset', status: 400, code: 'validation_failed' } };
    const range = reportRangeOf(preset, FAKE_REPORTS_TODAY);
    const previous = previousRangeOf(range);
    return { status: 200, body: { preset, from: range.from, to: range.to, previousFrom: previous.from, previousTo: previous.to, today: FAKE_REPORTS_TODAY, timeZone: 'Asia/Kolkata', businessDayCutoff: '06:00' } };
  }

  const from = searchParams.get('from') ?? '2026-09-25';
  const to = searchParams.get('to') ?? FAKE_REPORTS_TODAY;
  const venueParam = searchParams.get('venueId');
  const venues = venueParam === null ? VENUES : VENUES.filter((venue) => venue.venueId === venueParam);
  const inScope = venues.length > 0 ? venues : [{ venueId: venueParam ?? request.venueId, name: 'Playground Central', scale: 1 }];
  const scale = inScope.reduce((total, venue) => total + venue.scale, 0);
  const previous = previousRangeOf({ from, to });
  const scope = {
    from,
    to,
    previousFrom: previous.from,
    previousTo: previous.to,
    currency: 'INR',
    venues: inScope.map((venue) => ({ venueId: venue.venueId, name: venue.name })),
    partial: false,
  };
  const days = daysOf(from, to);

  if (which === 'revenue') {
    const current = days.map((day) => dayMoney(day, scale));
    const before = daysOf(previous.from, previous.to).map((day) => dayMoney(day, scale));
    const totals = breakdown(current);
    const revenue = totals.revenue.amount;
    const prior = breakdown(before);
    const ex = (share: number) => INR(revenue * share);
    return {
      status: 200,
      body: {
        ...scope,
        totals,
        previous: prior,
        byDay: current.map((day) => ({
          date: day.date,
          revenue: day.date > FAKE_REPORTS_TODAY ? null : INR(day.revenue),
          moneyIn: day.date > FAKE_REPORTS_TODAY ? null : INR(day.moneyIn),
          walletSpent: INR(day.walletSpent),
          walletTopUps: INR(day.topUps),
          bonusSpent: INR(day.bonusSpent),
        })),
        netSales: {
          // Σ categories − discounts − wallet credit given (the bonus contra is never a negative "Other").
          total: INR(ex(0.62).amount + ex(0.31).amount + ex(0.04).amount + ex(0.011).amount - ex(0.021).amount - ex(0.006).amount),
          discounts: ex(0.021),
          walletCredit: ex(0.006),
          byCategory: [
            { category: 'play', amount: ex(0.62) },
            { category: 'food', amount: ex(0.31) },
            { category: 'retail', amount: ex(0.04) },
            { category: 'membership', amount: INR(0) },
            { category: 'other', amount: ex(0.011) },
          ],
        },
        previousNetSales: INR(prior.revenue.amount * 0.97),
        // A little of the GST on wallet spends was paid at top-up, on balances moved in from a previous system.
        tax: { total: ex(0.046), byComponent: [{ component: 'CGST', amount: ex(0.023) }, { component: 'SGST', amount: ex(0.023) }], paidAtTopUp: ex(0.002) },
        refundRate: { numerator: totals.refunds.amount, denominator: totals.payments.amount, basisPoints: Math.round((totals.refunds.amount * 10_000) / Math.max(1, totals.payments.amount)) },
        wallet: {
          topUps: totals.walletTopUps,
          handedBack: totals.walletHandedBack,
          spent: totals.walletSpent,
          bonusGranted: totals.bonusGranted,
          bonusSpent: totals.bonusSpent,
          outstandingCash: INR(18_640_000),
          outstandingBonus: INR(1_210_000),
          passLiability: INR(4_385_000),
          receivablesOutstanding: INR(915_000),
        },
        byVenue: inScope.map((venue) => {
          const mine = breakdown(days.map((day) => dayMoney(day, venue.scale)));
          const mineBefore = breakdown(daysOf(previous.from, previous.to).map((day) => dayMoney(day, venue.scale * 0.94)));
          return { venueId: venue.venueId, name: venue.name, revenue: mine.revenue, moneyIn: mine.moneyIn, previousRevenue: mineBefore.revenue };
        }),
      },
    };
  }

  if (which === 'sessions') {
    const stations = Math.round(36 * scale);
    const occupancy = (weekday: number, hour: number, salt: string): number | null => {
      // Open 10:00 to 02:00: the small hours after 02:00 and the morning are closed.
      if (hour >= 2 && hour < 10) return null;
      const evening = hour >= 18 || hour < 2 ? 0.62 + (hour >= 20 || hour < 1 ? 0.18 : 0) : hour >= 14 ? 0.42 : 0.24;
      const weekend = weekday >= 4 ? 0.14 : 0;
      return Math.round(Math.min(0.97, (evening + weekend) * (0.85 + 0.25 * noise(`${salt}${weekday}-${hour}`))) * 10_000);
    };
    const span = days.filter((day) => day <= FAKE_REPORTS_TODAY);
    const weeks = Math.max(1, span.length / 7);
    const heatmap = Array.from({ length: 7 }, (_, weekday) =>
      Array.from({ length: 24 }, (_, hour) => {
        const basisPoints = span.length === 0 ? null : occupancy(weekday, hour, 'h');
        const available = basisPoints === null ? 0 : Math.round(stations * 3600 * weeks);
        return { weekday, hour, inSessionSeconds: basisPoints === null ? 0 : Math.round((available * basisPoints) / 10_000), availableSeconds: available, basisPoints };
      }),
    ).flat();
    const inSession = heatmap.reduce((total, cell) => total + cell.inSessionSeconds, 0);
    const available = heatmap.reduce((total, cell) => total + cell.availableSeconds, 0);
    const byDay = days.map((date) => {
      const sessions = date > FAKE_REPORTS_TODAY ? 0 : Math.round(62 * scale * (WEEKDAY_FACTOR[weekdayOf(date)] ?? 1) * (0.85 + 0.3 * noise(`s${date}`)));
      return { date, sessions, playSeconds: sessions * Math.round(5400 + 1800 * noise(`p${date}`)) };
    });
    const sessions = byDay.reduce((total, day) => total + day.sessions, 0);
    const playSeconds = byDay.reduce((total, day) => total + day.playSeconds, 0);
    const figures = (factor: number) => ({
      sessions: sessions === 0 ? null : Math.round(sessions * factor),
      playSeconds: sessions === 0 ? null : Math.round(playSeconds * factor),
      averageSeconds: sessions === 0 ? null : Math.round(playSeconds / sessions),
      activePlayers: sessions === 0 ? null : Math.round(sessions * 0.41 * factor),
      newPlayers: sessions === 0 ? null : Math.round(sessions * 0.07 * factor),
      inSessionSeconds: Math.round(inSession * factor),
      availableSeconds: available,
      occupancyBasisPoints: available === 0 ? null : Math.round((inSession * factor * 10_000) / available),
    });
    const byHour = Array.from({ length: 24 }, (_, hour) => {
      const cells = heatmap.filter((cell) => cell.hour === hour);
      const hourIn = cells.reduce((total, cell) => total + cell.inSessionSeconds, 0);
      const hourAvailable = cells.reduce((total, cell) => total + cell.availableSeconds, 0);
      return { hour, inSessionSeconds: hourIn, availableSeconds: hourAvailable, basisPoints: hourAvailable === 0 ? null : Math.round((hourIn * 10_000) / hourAvailable) };
    });
    const peaks = [...heatmap].filter((cell) => cell.basisPoints !== null).sort((a, b) => (b.basisPoints ?? 0) - (a.basisPoints ?? 0)).slice(0, 3);
    const group = (id: string | null, name: string | null, count: number, bp: number) => ({ id, name, stations: count, inSessionSeconds: Math.round((available / stations) * count * (bp / 10_000)), availableSeconds: Math.round((available / stations) * count), basisPoints: bp });
    return {
      status: 200,
      body: {
        ...scope,
        totals: figures(1),
        previous: figures(0.92),
        byDay,
        firstHour: 6,
        heatmap,
        byHour,
        peaks,
        byClass: [
          group('stc_01k5znw4hmfay8vvr14d2pf6a1', 'Pro PCs', Math.round(stations * 0.33), 6840),
          group('stc_01k5znw4hmfay8vvr14d2pf6a2', 'Standard PCs', Math.round(stations * 0.56), 5120),
          group('stc_01k5znw4hmfay8vvr14d2pf6a3', 'PS5 bays', Math.round(stations * 0.11), 3790),
        ],
        byZone: [
          group('zon_01k5znw4hmfay8vvr14d2pf6b1', 'Bootcamp', Math.round(stations * 0.22), 7210),
          group('zon_01k5znw4hmfay8vvr14d2pf6b2', 'Main floor', Math.round(stations * 0.56), 5240),
          group('zon_01k5znw4hmfay8vvr14d2pf6b3', 'VIP room', Math.round(stations * 0.11), 6020),
          group(null, null, Math.round(stations * 0.11), 3790),
        ],
        hoursSet: true,
      },
    };
  }

  const reach = days.filter((day) => day <= FAKE_REPORTS_TODAY).length * scale;
  if (which === 'products') {
    const item = (name: string, kind: 'food' | 'retail' | 'other', perDay: number, price: number) => {
      const quantity = Math.round(perDay * reach);
      return { name, kind, quantity, net: INR(quantity * price), orders: Math.round(quantity * 0.86) };
    };
    const items = [
      item('Cold coffee', 'food', 14, 12_712),
      item('Masala chai', 'food', 31, 2_966),
      item('Peri-peri fries', 'food', 9, 12_712),
      item('Paneer roll', 'food', 6, 16_102),
      item('Maggi', 'food', 11, 7_627),
      item('Red Bull 250 ml', 'food', 8, 10_169),
      item('Mountain Dew', 'food', 13, 3_390),
      item('Headset rental', 'other', 3, 5_000),
      item('Mouse pad', 'retail', 0.4, 29_900),
    ].filter((row) => row.quantity > 0);
    items.sort((a, b) => b.net.amount - a.net.amount);
    const pass = (name: string, perDay: number, granted: number, price: number) => {
      const sold = Math.round(perDay * reach);
      return { name, sold, granted: Math.round(granted * reach), revenue: INR(sold * price) };
    };
    const passes = [pass('Night pack · 4 h', 3.3, 0, 24_900), pass('Happy hours · 2 h', 2.6, 0.1, 9_900), pass('Bootcamp · 3 h', 1.6, 0, 21_000), pass('Weekend 10 h', 0.5, 0.05, 59_900)].filter((row) => row.sold + row.granted > 0);
    const passRevenue = passes.reduce((total, row) => total + row.revenue.amount, 0);
    return { status: 200, body: { ...scope, items, passes, play: { passes: INR(passRevenue), rateCard: INR(1_120_000 * reach), discounts: INR(36_000 * reach) } } };
  }

  // Staff.
  const member = (memberId: string | null, name: string | null, share: number, voids: number, comps: number) => ({
    memberId,
    name,
    revenue: INR(1_400_000 * reach * share),
    refunds: INR(16_000 * reach * share),
    topUps: memberId === null ? INR(0) : INR(480_000 * reach * share),
    discounts: INR(30_000 * reach * share),
    voids: Math.round(voids * Math.max(1, reach / 7)),
    voidedValue: INR(Math.round(voids * Math.max(1, reach / 7)) * 18_000),
    comps: Math.round(comps * Math.max(1, reach / 7)),
    compSeconds: Math.round(comps * Math.max(1, reach / 7)) * 1800,
  });
  return {
    status: 200,
    body: {
      ...scope,
      members: [
        member('mem_01k5znw4hmfay8vvr14d2pf7a1', 'Riya Sharma', 0.38, 2, 3),
        member('mem_01k5znw4hmfay8vvr14d2pf7a2', 'Aman Verma', 0.29, 1, 1),
        member('mem_01k5znw4hmfay8vvr14d2pf7a3', 'Kabir Mehta', 0.17, 0, 2),
        member(null, null, 0.16, 0, 0),
      ],
    },
  };
}
